import type {
  ListResult,
  NmosDiscoveredFeed,
  NmosDiscoveredReceiver,
  NmosDiscoveredSender,
  NmosSenderKind,
  NmosDomainCreate,
  NmosDomainFilter,
  NmosDomainOperationFilter,
  NmosDomainSettings,
  NmosAdministrationFilter,
  NmosDiscoveryFilter,
  NmosDiscoveryAdmission,
  NmosTrustProfileCreate,
  NmosTrustProfileSettings,
  NmosCertificateAuthorityUpload,
} from 'common/api/v1/types'
import type { EdgeClient } from 'common/generated/edgeClient'
import type { PaginatedRequestParams } from '../nm-types'
import { singleSortQueryFromPaginatedRequestParams } from '../nm-types'

export class NmosApi {
  constructor(private readonly edgeClient: EdgeClient) {}

  listDomains(filter: NmosDomainFilter = {}, skip = 0, limit = 50) {
    return this.edgeClient.getNmosDomainList({ filter, skip, limit })
  }

  getDomain(id: string) {
    return this.edgeClient.getNmosDomains(id)
  }

  listDomainOperations(id: string, filter: NmosDomainOperationFilter = {}, skip = 0, limit = 10) {
    return this.edgeClient.getNmosDomainOperationList(id, { filter, skip, limit })
  }

  createDomain(domain: NmosDomainCreate) {
    return this.edgeClient.domainsNmos(domain)
  }

  updateDomain(id: string, settings: NmosDomainSettings) {
    return this.edgeClient.setNmosDomains(id, settings)
  }

  deleteDomain(id: string) {
    return this.edgeClient.deleteNmosDomains(id)
  }

  assignDomain(applianceId: string, portId: string, domainId: string | null) {
    return this.edgeClient.setAppliancePortsNmosDomain(applianceId, portId, { domainId })
  }

  listDiscovery(filter: NmosDiscoveryFilter = {}, skip = 0, limit = 50) {
    return this.edgeClient.getNmosDiscoveryList({ filter, skip, limit })
  }

  admitDiscovery(value: NmosDiscoveryAdmission) {
    return this.edgeClient.admitNmosDiscoveries(value)
  }

  listTrustProfiles(filter: NmosAdministrationFilter = {}, skip = 0, limit = 50) {
    return this.edgeClient.getNmosTrustProfileList({ filter, skip, limit })
  }

  getTrustProfile(id: string) {
    return this.edgeClient.getNmosTrustProfiles(id)
  }

  createTrustProfile(value: NmosTrustProfileCreate) {
    return this.edgeClient.trustProfilesNmos(value)
  }

  updateTrustProfile(id: string, value: NmosTrustProfileSettings) {
    return this.edgeClient.setNmosTrustProfiles(id, value)
  }

  deleteTrustProfile(id: string) {
    return this.edgeClient.deleteNmosTrustProfiles(id)
  }

  listCertificates(filter: NmosAdministrationFilter = {}, skip = 0, limit = 50) {
    return this.edgeClient.getNmosCertificateAuthorityList({ filter, skip, limit })
  }

  uploadCertificates(value: NmosCertificateAuthorityUpload) {
    return this.edgeClient.certificateAuthoritiesNmos(value)
  }

  deleteCertificate(id: string) {
    return this.edgeClient.deleteNmosCertificateAuthorities(id)
  }

  listSenders(
    params: PaginatedRequestParams & { physicalPort: string; kind?: NmosSenderKind },
  ): Promise<ListResult<NmosDiscoveredSender>> {
    return this.edgeClient.listNmosSenders(
      singleSortQueryFromPaginatedRequestParams({
        filter: { searchName: params.filter, kind: params.kind, physicalPort: params.physicalPort },
        paginatedRequestParams: params,
      }),
    )
  }

  listInputFeeds(params: PaginatedRequestParams & { physicalPort: string }): Promise<ListResult<NmosDiscoveredFeed>> {
    return this.edgeClient.listNmosInputFeeds(
      singleSortQueryFromPaginatedRequestParams({
        filter: { searchName: params.filter, physicalPort: params.physicalPort },
        paginatedRequestParams: params,
      }),
    )
  }

  listReceivers(
    params: PaginatedRequestParams & { physicalPort: string },
  ): Promise<ListResult<NmosDiscoveredReceiver>> {
    return this.edgeClient.listNmosReceivers(
      singleSortQueryFromPaginatedRequestParams({
        filter: { searchName: params.filter, physicalPort: params.physicalPort },
        paginatedRequestParams: params,
      }),
    )
  }
}
